import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../components/Loader";
import CountryAccessPicker, { mergeAccessWithCountries } from "../components/CountryAccessPicker";
import UserPermissionsPanel from "../components/UserPermissionsPanel";
import { crmHeaders } from "../utils/crmApi";
import { EMPTY_USER_PERMISSIONS } from "../utils/userAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function EditUserAccess() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [access, setAccess] = useState([]);
    const [userPermissions, setUserPermissions] = useState({ ...EMPTY_USER_PERMISSIONS });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            axios.get(`${BASE_URL}/getusers`, { headers: crmHeaders() }),
            axios.get(`${BASE_URL}/getcountries`, { headers: crmHeaders() }),
            axios.get(`${BASE_URL}/user-country-access/${id}`, { headers: crmHeaders() }),
        ])
            .then(([usersRes, countriesRes, accessRes]) => {
                const found = usersRes.data.find((u) => String(u.Id) === String(id));
                const active = countriesRes.data.filter((c) => c.Status === "Active");
                setUser(found || null);
                setAccess(mergeAccessWithCountries(active, accessRes.data.access || []));
                setUserPermissions({
                    ...EMPTY_USER_PERMISSIONS,
                    ...(accessRes.data.userPermissions || {}),
                });
                setLoading(false);
            })
            .catch(() => {
                toast.error("Failed to load user access");
                setLoading(false);
            });
    }, [id]);

    const handleSave = (e) => {
        e.preventDefault();
        if (!user) return;

        const selected = access.filter(
            (a) => a.leads || (Array.isArray(a.emailIds) && a.emailIds.length > 0)
        );
        if (!selected.length) {
            toast.error("Select at least one country permission");
            return;
        }

        setSaving(true);
        axios
            .put(
                `${BASE_URL}/users/${id}/country-access`,
                { CountryAccess: selected, UserPermissions: userPermissions },
                { headers: crmHeaders() }
            )
            .then(() => {
                toast.success("User access updated");
                navigate("/users");
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || "Failed to update access");
            })
            .finally(() => setSaving(false));
    };

    if (loading) return <Loader />;
    if (!user) {
        return (
            <div className="container">
                <p>User not found.</p>
                <Link to="/users">Back to Users</Link>
            </div>
        );
    }

    if (user.Role === "Administrator") {
        return (
            <div className="container">
                <p>Administrator accounts have full access to all countries.</p>
                <Link to="/users">Back to Users</Link>
            </div>
        );
    }

    return (
        <div className="container crm-user-form-page">
            <ToastContainer />
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Edit Access — {user.Name}</h1>
                    <p className="crm-page-subtitle">{user.Email}</p>
                </div>
                <Link to="/users" className="btn btn-outline-secondary">
                    <i className="bi bi-arrow-left me-1"></i> Back
                </Link>
            </div>

            <form onSubmit={handleSave} className="crm-user-form">
                <div className="crm-user-form-card">
                    <div className="crm-user-form-card-header">
                        <i className="bi bi-globe2"></i>
                        <div>
                            <h5>Country Access</h5>
                            <span>Leads and email accounts per country</span>
                        </div>
                    </div>
                    <div className="crm-user-form-card-body">
                        <CountryAccessPicker access={access} onChange={setAccess} />
                    </div>
                </div>

                <div className="crm-user-form-card">
                    <div className="crm-user-form-card-body pt-0">
                        <UserPermissionsPanel
                            permissions={userPermissions}
                            onChange={setUserPermissions}
                        />
                    </div>
                </div>

                <div className="crm-user-form-actions">
                    <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
                        {saving ? "Saving..." : "Save Access"}
                    </button>
                    <Link to="/users" className="btn btn-outline-secondary btn-lg">
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
}
